import { useCountdown, ProgressBar, useNow } from "@/lib/time";
import { spriteUrl } from "@/lib/api";
import { Compass, Swords, Dumbbell, Crosshair, Bed, Trophy, Sparkles, Zap, Repeat, Backpack } from "lucide-react";

const PERSONALITY_LABELS = {
  battler: "Combattant",
  explorer: "Explorateur",
  collector: "Collectionneur",
  balanced: "Équilibré",
};

const ACTION_ICONS = {
  explore: Compass,
  capture: Crosshair,
  train: Dumbbell,
  gym: Swords,
  league: Trophy,
  legendary: Sparkles,
  rest: Bed,
};

const ACTION_LABELS = {
  explore: "Exploration",
  capture: "Capture",
  train: "Entraînement",
  gym: "Arène",
  league: "Ligue Pokémon",
  legendary: "Recherche Légendaire",
  rest: "Repos",
};

const MAX_LEGENDARIES = 3;

export default function TrainerCard({ trainer, locationsRef, gymsRef, onStrategy, onDismiss, onOpenDetail, onOpenBag }) {
  const { label: timer, urgent } = useCountdown(trainer.action_ends_at);
  const now = useNow(1000);
  const Icon = ACTION_ICONS[trainer.current_action] || Bed;
  const target = trainer.action_target
    ? (locationsRef?.[trainer.action_target]?.name
        || gymsRef?.find?.((g) => g.id === trainer.action_target)?.name
        || (trainer.action_target === "hunt" ? "Quête Légendaire" : trainer.action_target))
    : null;

  const isMaster = trainer.league_status === "completed";
  const legendariesCount = trainer.legendaries_caught?.length || 0;
  const canHunt = isMaster && legendariesCount < MAX_LEGENDARIES;

  return (
    <div
      className="panel p-3 fade-up cursor-pointer hover:bg-[#FCF9F2]"
      data-testid={`trainer-card-${trainer.id}`}
      onClick={(e) => {
        if (e.target.closest("button, select")) return;
        onOpenDetail?.(trainer.id);
      }}
    >
      <div className="flex justify-between items-start gap-2">
        <div className="min-w-0 flex items-start gap-2">
          <img
            src={`https://play.pokemonshowdown.com/sprites/trainers/${trainer.sprite_id || "youngster"}.png`}
            alt={trainer.name}
            className="pixelated w-14 h-14 shrink-0 -mt-1"
            style={{ imageRendering: "pixelated" }}
            title={trainer.name}
            data-testid={`trainer-avatar-${trainer.id}`}
            onError={(e) => { e.currentTarget.src = "https://play.pokemonshowdown.com/sprites/trainers/youngster.png"; }}
          />
          <div className="min-w-0">
            <div className="overline truncate" data-testid={`trainer-personality-${trainer.id}`}>
              {PERSONALITY_LABELS[trainer.personality]} · Niv {trainer.level}
            </div>
            <div className="font-serif-display text-lg leading-tight truncate" data-testid={`trainer-name-${trainer.id}`}>
              {trainer.name}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-wrap justify-end">
          {onOpenBag && (
            <button
              data-testid={`trainer-bag-btn-${trainer.id}`}
              onClick={(e) => { e.stopPropagation(); onOpenBag(trainer.id); }}
              className="btn-ink text-[10px] py-1 px-1.5"
              title={`Sac de ${trainer.name}`}
            >
              <Backpack size={12}/>
            </button>
          )}
          {trainer.badges?.length > 0 && (
            <span className="font-mono text-[10px] border border-[#B87333] text-[#B87333] px-1.5 py-0.5" data-testid={`trainer-badges-${trainer.id}`}>
              {trainer.badges.length}/8 🏅
            </span>
          )}
          {trainer.has_shiny_charm && (
            <span className="font-mono text-[9px] border border-[#B87333] text-[#B87333] px-1 py-0.5" title="Charme Chromatique">💎</span>
          )}
          {isMaster && (
            <span className="stamp text-[9px]" style={{transform:"rotate(2deg)"}}>MAÎTRE</span>
          )}
        </div>
      </div>

      <div className="mt-2 flex items-center gap-2 text-xs">
        <Icon size={13} className="text-[#4A4A4A]"/>
        <span className="font-mono uppercase tracking-wider">{ACTION_LABELS[trainer.current_action]}</span>
        {target && <span className="text-[#4A4A4A] truncate">· {target}</span>}
        {(() => {
          const boostUntil = trainer.speed_boost_until;
          const mult = trainer.speed_boost_mult;
          if (!boostUntil || !mult) return null;
          const remainMs = new Date(boostUntil).getTime() - now;
          if (remainMs <= 0) return null;
          const remainMin = Math.ceil(remainMs / 60000);
          return (
            <span
              className="font-mono text-[9px] border border-[#B87333] bg-[#FCF9F2] text-[#B87333] px-1 py-0.5 inline-flex items-center gap-0.5"
              data-testid={`trainer-boost-${trainer.id}`}
              title={`Vitesse ×${mult} — encore ${remainMin} min`}
            >
              <Zap size={9}/>×{mult} {remainMin}m
            </span>
          );
        })()}
        {(() => {
          const repeat = trainer.forced_next_repeat;
          const forced = trainer.forced_next_action;
          // repeat = future forced runs queued after the current action.
          if (!forced || !repeat || repeat < 1) return null;
          return (
            <span
              className="font-mono text-[9px] border border-[#2E4F32] bg-[#F0F5E7] text-[#2E4F32] px-1 py-0.5 inline-flex items-center gap-0.5"
              data-testid={`trainer-repeat-${trainer.id}`}
              title={`Encore ${repeat} exécution(s) forcée(s) après l'action en cours`}
            >
              <Repeat size={9}/>+{repeat}
            </span>
          );
        })()}
        <span className={`ml-auto font-mono ${urgent ? "timer-urgent" : ""}`} data-testid={`trainer-timer-${trainer.id}`}>{timer}</span>
      </div>
      <ProgressBar startIso={trainer.action_started_at} endIso={trainer.action_ends_at}/>

      <div className="mt-2">
        <div className="flex justify-between text-[10px] font-mono text-[#4A4A4A]">
          <span>XP</span>
          <span>{trainer.xp}/{trainer.level * 50}</span>
        </div>
        <div className="h-[3px] bg-[#EAE4D9] border border-[#2B2B2B] mt-0.5">
          <div className="h-full bg-[#2E4F32]" style={{width: `${Math.min(100, (trainer.xp / (trainer.level * 50)) * 100)}%`}}/>
        </div>
      </div>

      {/* Stamina bar */}
      {(() => {
        const stamina = trainer.stamina ?? 100;
        const staminaColor = stamina >= 60 ? "#2E4F32" : stamina >= 30 ? "#B87333" : "#8C2A2A";
        return (
          <div className="mt-1.5" data-testid={`trainer-stamina-${trainer.id}`}>
            <div className="flex justify-between text-[10px] font-mono text-[#4A4A4A]">
              <span>Stamina</span>
              <span style={{color: staminaColor}}>{stamina}/100</span>
            </div>
            <div className="h-[3px] bg-[#EAE4D9] border border-[#2B2B2B] mt-0.5">
              <div className="h-full transition-all" style={{width: `${stamina}%`, backgroundColor: staminaColor}}/>
            </div>
          </div>
        );
      })()}

      {/* Legendaries (Masters only) */}
      {isMaster && (
        <div className="mt-2 flex items-center gap-2 text-[11px] font-mono" data-testid={`trainer-legendaries-${trainer.id}`}>
          <span className="text-[#B87333]">🌟 Légendaires {legendariesCount}/{MAX_LEGENDARIES}</span>
          <span className="inline-flex gap-1">
            {trainer.legendaries_caught?.map((pid) => (
              <img key={pid} src={spriteUrl(pid)} alt="" className="w-5 h-5 pixelated"/>
            ))}
          </span>
        </div>
      )}

      {/* Team count */}
      <div className="mt-2 text-[11px] font-mono text-[#4A4A4A]" data-testid={`trainer-team-${trainer.id}`}>
        Équipe : {trainer.team?.length || 0}/6
        {trainer.team?.length > 0 && (
          <span className="ml-2 inline-flex gap-1 align-middle">
            {trainer.team.slice(0, 6).map((p, i) => (
              <img key={i} src={spriteUrl(p.pokemon_id)} alt={p.name} className="w-5 h-5 pixelated inline-block" />
            ))}
          </span>
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-1 items-center">
        <span className="overline text-[9px] mr-1">Forcer</span>
        <button
          data-testid={`force-explore-${trainer.id}`}
          className="btn-ink text-[10px] py-1 px-2"
          title="Imposer une exploration (1 fois, retour en auto ensuite)"
          onClick={() => onStrategy(trainer.id, "explore")}
        >Explore</button>
        <button
          data-testid={`force-capture-${trainer.id}`}
          className="btn-ink text-[10px] py-1 px-2"
          onClick={() => onStrategy(trainer.id, "capture")}
        >Capture</button>
        <button
          data-testid={`force-train-${trainer.id}`}
          className="btn-ink text-[10px] py-1 px-2"
          onClick={() => onStrategy(trainer.id, "train")}
        >Train</button>
        <button
          data-testid={`force-gym-${trainer.id}`}
          className="btn-ink text-[10px] py-1 px-2"
          onClick={() => onStrategy(trainer.id, "gym")}
        >Arène</button>
        <button
          data-testid={`force-rest-${trainer.id}`}
          className="btn-ink text-[10px] py-1 px-2"
          title="Forcer un repos (sera placé en prochaine action)"
          onClick={() => onStrategy(trainer.id, "rest")}
        >Repos</button>
        {canHunt && (
          <button
            data-testid={`force-legendary-${trainer.id}`}
            className="btn-ink text-[10px] py-1 px-2 text-[#B87333] border-[#B87333]"
            onClick={() => onStrategy(trainer.id, "legendary")}
          >🌟</button>
        )}
        <button
          data-testid={`trainer-dismiss-${trainer.id}`}
          className="btn-ink btn-accent text-[10px] py-1 px-2 ml-auto"
          onClick={() => onDismiss(trainer.id)}
          title="Renvoyer ce dresseur"
        >✕</button>
      </div>
    </div>
  );
}
